// Past screen: FED / sleep / HRV history at company, region, group and
// individual scope (prototype renderPast).

import React from 'react';
import { cn } from '@/lib/utils';
import {
  DEMO_MODE,
  GROUPS,
  REGIONS,
  WORKERS,
  avgFED,
  notReadyCount,
  pct,
  readiness,
  rdyLabel,
  sign,
  wArr,
  workersInGroup,
  workersInRegion,
  wtrBreaches,
} from '../engine/data';
import type { Worker } from '../engine/data';
import { genInsight } from '../engine/insights';
import { buildLivePastCfg } from '../engine/live';
import { rdyTone } from '../engine/scores';
import { AiInsight } from '../AiPanels';
import { CompBar } from '../ScoreWidgets';
import { useDash } from '../state';
import { Chip, Dot, Grid, Kpi, KpiRow, Panel, Pill, dataTable, fedTone, toneText, toneVar } from '../ui';
import type { Tone } from '../ui';
import { PAST_CFG } from './past/config';
import type { PastCfg, PastRange } from './past/config';
import { ScopeSwitchers } from './past/ScopePickers';
import { TrendChart } from './past/TrendChart';

const th = cn(dataTable.th, 'text-center');
const tdC = cn(dataTable.td, 'text-center');
const clickRow = cn(dataTable.tr, 'cursor-pointer');

const avgSleepOf = (ws: Worker[]) => (ws.reduce((a, w) => a + (w.hrv_b ? 6.2 : 5.8), 0) / ws.length).toFixed(1);

/** Higher-is-worse metric → tone (e.g. RHR delta, consecutive days). */
const highBad = (v: number, bad: number, warn: number): Tone => (v >= bad ? 'bad' : v >= warn ? 'warn' : 'good');
/** Lower-is-worse metric → tone (e.g. HRV / sleep delta). */
const lowBad = (v: number, bad: number, warn: number): Tone => (v <= bad ? 'bad' : v <= warn ? 'warn' : 'good');

const HeatPill: React.FC<{ v: number | undefined; strong?: boolean }> = ({ v, strong }) =>
  v === undefined ? (
    <span className='text-muted-foreground'>–</span>
  ) : (
    <Pill tone={fedTone(v)} className={cn('min-w-9 justify-center border-transparent', strong && 'font-bold')}>
      {v}
    </Pill>
  );

/** A worker's value for heat column `i`: the live period series, or the prototype's weekly FED. */
const workerAt = (cfg: PastCfg, w: Worker, i: number): number | undefined =>
  cfg.workerSeries ? cfg.workerSeries[w.init]?.[i] : w.fed12[i];

const NeedsRota: React.FC = () => (
  <p className='py-6 text-center text-sm text-muted-foreground'>
    Needs rota data (shift times and driving) to split in-work from out-of-work fatigue.
  </p>
);

const SplitBar: React.FC<{ inWork: number; outWork: number; tall?: boolean }> = ({ inWork, outWork, tall }) => (
  <div className={cn('flex w-full overflow-hidden rounded-md bg-muted', tall ? 'h-5' : 'h-2')}>
    <div className='h-full bg-info' style={{ width: `${inWork}%` }} />
    <div className='h-full bg-violet' style={{ width: `${outWork}%` }} />
  </div>
);

const SplitLegend: React.FC = () => (
  <div className='flex gap-4 text-[11px] text-muted-foreground'>
    <span className='flex items-center gap-1'>
      <span className='inline-block h-2.5 w-2.5 rounded-sm bg-info' />
      In-work
    </span>
    <span className='flex items-center gap-1'>
      <span className='inline-block h-2.5 w-2.5 rounded-sm bg-violet' />
      Out-of-work
    </span>
  </div>
);

const SplitHeader: React.FC<{ name: string; inWork: number; outWork: number }> = ({ name, inWork, outWork }) => (
  <div className='mb-1.5 flex justify-between'>
    <span className='text-[13px] font-medium'>{name}</span>
    <span className='text-[11px] text-muted-foreground'>
      <span className='text-info'>In {inWork}%</span> · <span className='text-violet'>Out {outWork}%</span>
    </span>
  </div>
);

const RegionLegend: React.FC<{ highlight?: string | null }> = ({ highlight }) => (
  <div className='mt-2 flex flex-wrap gap-4'>
    {Object.entries(REGIONS).map(([rid, reg]) => (
      <span
        key={rid}
        className={cn(
          'flex items-center gap-1 text-[11px] text-muted-foreground',
          highlight === rid && 'font-semibold',
        )}
        style={highlight === rid ? { color: reg.color } : undefined}
      >
        <Dot color={reg.color} className='h-2.5 w-2.5' />
        {reg.name}
      </span>
    ))}
  </div>
);

const RegionTrendChart: React.FC<{ cfg: PastCfg }> = ({ cfg }) => (
  <TrendChart
    labels={cfg.xLabels}
    min={20}
    max={90}
    height={180}
    series={Object.entries(REGIONS).map(([rid, reg]) => ({
      key: rid,
      name: reg.name,
      data: cfg.regionTrends[rid],
      color: reg.color,
    }))}
  />
);

const PeriodFilter: React.FC<{ range: PastRange }> = ({ range }) => {
  const { patch } = useDash();
  return (
    <div className='flex flex-wrap items-center gap-2'>
      <span className='text-xs font-medium text-muted-foreground'>Period:</span>
      {(['7d', '30d', '90d'] as const).map((k) => (
        <Chip key={k} active={range === k} onClick={() => patch({ pastRange: k })}>
          {k.replace('d', ' days')}
        </Chip>
      ))}
    </div>
  );
};

const HeatHead: React.FC<{ cfg: PastCfg; lead: string[]; readinessCol?: boolean }> = ({ cfg, lead, readinessCol }) => (
  <thead>
    <tr>
      {lead.map((l) => (
        <th key={l} className={dataTable.th}>
          {l}
        </th>
      ))}
      {cfg.heatCols.map((c) => (
        <th key={c} className={th}>
          {c}
        </th>
      ))}
      <th className={th}>Now</th>
      {readinessCol && <th className={th}>Readiness</th>}
    </tr>
  </thead>
);

const SleepHrvCharts: React.FC<{ cfg: PastCfg }> = ({ cfg }) => {
  const n = cfg.xLabels.length;
  return (
    <Grid cols={2}>
      <Panel title='Sleep Duration — Company Average' sub={`vs 7.0h target · ${cfg.subLabel}`}>
        <TrendChart
          labels={cfg.xLabels}
          min={4}
          max={9}
          height={160}
          unit='h'
          series={[
            {
              key: 'target',
              name: 'Target',
              data: new Array<number>(n).fill(7.0),
              color: toneVar.good,
              reference: true,
            },
            { key: 'sleep', name: 'Sleep', data: cfg.slpTrend, color: toneVar.info, area: true },
          ]}
        />
      </Panel>
      <Panel title='HRV — Company Baseline Deviation' sub={`% vs individual 28-day baselines · ${cfg.subLabel}`}>
        <TrendChart
          labels={cfg.xLabels}
          min={-25}
          max={5}
          height={160}
          unit='%'
          series={[
            { key: 'base', name: 'Baseline', data: new Array<number>(n).fill(0), color: toneVar.good, reference: true },
            { key: 'hrv', name: 'HRV deviation', data: cfg.hrvTrend, color: toneVar.bad, area: true },
          ]}
        />
      </Panel>
    </Grid>
  );
};

// ── Company ──────────────────────────────────────────────────────────────────

const CompanyPast: React.FC<{ cfg: PastCfg }> = ({ cfg }) => {
  const { drillRegion } = useDash();
  const allW = wArr();
  const peakW = [...allW].sort((a, b) => b.fed - a.fed)[0];
  const compAvg = avgFED(allW);
  const totalWtr = wtrBreaches(allW);
  const delta = cfg.kpi.fedDelta;
  return (
    <>
      <KpiRow>
        <Kpi
          label={`Peak FED — ${cfg.subLabel}`}
          value={peakW.fed}
          sub={GROUPS[peakW.group].name}
          subTrend='up'
          tone={peakW.fed >= 80 ? 'bad' : 'warn'}
        />
        <Kpi
          label='Company Avg FED'
          value={compAvg}
          sub={`${delta > 0 ? '↑ +' : '↓ '}${delta} vs prior period`}
          subTrend={delta > 0 ? 'up' : 'dn'}
          tone={delta > 4 ? 'warn' : undefined}
        />
        <Kpi label='WTR Violations' value={totalWtr} sub={cfg.subLabel} tone={totalWtr > 0 ? 'bad' : 'good'} />
        <Kpi
          label='Avg Sleep Duration'
          value={`${avgSleepOf(allW)}h`}
          sub='↓ vs 7.0h target'
          subTrend='up'
          tone='warn'
        />
      </KpiRow>
      <AiInsight>{genInsight('past', allW, { isCompany: true, scopeName: 'the company' })}</AiInsight>
      <Grid cols={2}>
        <Panel title='FED Trend by Region' sub={`${cfg.subLabel} — Fatigue Exposure Dose`}>
          <RegionTrendChart cfg={cfg} />
          <RegionLegend />
        </Panel>
        <Panel title='In-Work vs Out-of-Work Fatigue' sub={`Fatigue source by region · ${cfg.subLabel}`}>
          {DEMO_MODE.live ? (
            <NeedsRota />
          ) : (
            <div className='flex flex-col gap-3.5'>
              {Object.entries(REGIONS).map(([rid, reg]) => {
                const [iw, ow] = cfg.inOutData[rid];
                return (
                  <div key={rid}>
                    <SplitHeader name={reg.name} inWork={iw} outWork={ow} />
                    <SplitBar inWork={iw} outWork={ow} />
                  </div>
                );
              })}
              <SplitLegend />
            </div>
          )}
        </Panel>
      </Grid>
      <SleepHrvCharts cfg={cfg} />
      <Panel title='Region Performance Summary' sub={`Aggregate scores — ${cfg.subLabel}`}>
        <div className={dataTable.wrap}>
          <table className={dataTable.table}>
            <thead>
              <tr>
                <th className={dataTable.th}>Region</th>
                <th className={th}>Workers</th>
                <th className={th}>Avg FED</th>
                <th className={th}>Not Ready</th>
                <th className={th}>WTR</th>
                {cfg.heatCols.map((c) => (
                  <th key={c} className={th}>
                    {c}
                  </th>
                ))}
                <th className={th}>Now</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(REGIONS).map(([rid, reg]) => {
                const rw = workersInRegion(rid);
                const rFED = avgFED(rw);
                const rNR = notReadyCount(rw);
                const rWTR = wtrBreaches(rw);
                return (
                  <tr key={rid} className={clickRow} onClick={() => drillRegion(rid)}>
                    <td className={dataTable.td}>
                      <span className='flex items-center gap-1.5'>
                        <Dot color={reg.color} />
                        <strong>{reg.name}</strong>
                      </span>
                    </td>
                    <td className={tdC}>{rw.length}</td>
                    <td className={cn(tdC, 'font-semibold', toneText[fedTone(rFED)])}>{rFED}</td>
                    <td className={cn(tdC, rNR > 0 ? 'text-destructive' : 'text-success')}>{rNR}</td>
                    <td className={cn(tdC, rWTR > 0 ? 'text-warning' : 'text-success')}>{rWTR}</td>
                    {cfg.heatIdx.map((i) => (
                      <td key={i} className={tdC}>
                        <HeatPill v={cfg.regionTrends[rid]?.[i]} />
                      </td>
                    ))}
                    <td className={tdC}>
                      <HeatPill v={rFED} strong />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
};

// ── Region ───────────────────────────────────────────────────────────────────

const RegionPast: React.FC<{ cfg: PastCfg; regionId: string }> = ({ cfg, regionId }) => {
  const { drillGroup, patch } = useDash();
  const reg = REGIONS[regionId];
  const rw = workersInRegion(regionId);
  const rAvg = avgFED(rw);
  const rWtr = wtrBreaches(rw);
  const rNR = notReadyCount(rw);
  return (
    <>
      <KpiRow>
        <Kpi
          label='Not Ready'
          value={
            <>
              {rNR}
              <small className='text-base font-normal text-muted-foreground'>/{rw.length}</small>
            </>
          }
          sub={reg.name}
          tone={rNR > 0 ? 'bad' : 'good'}
        />
        <Kpi label={`Avg FED — ${cfg.subLabel}`} value={rAvg} sub={`vs company avg ${avgFED(wArr())}`} tone='warn' />
        <Kpi label='WTR Violations' value={rWtr} sub={cfg.subLabel} tone={rWtr > 0 ? 'bad' : 'good'} />
        <Kpi label='Avg Sleep' value={`${avgSleepOf(rw)}h`} sub='↓ vs 7.0h target' subTrend='up' tone='warn' />
      </KpiRow>
      <AiInsight>{genInsight('past', rw, { regionId, scopeName: reg.name })}</AiInsight>
      <Grid cols={2}>
        <Panel title='All Regions — FED Trend' sub={`Comparison · ${cfg.subLabel}`}>
          <RegionTrendChart cfg={cfg} />
          <RegionLegend highlight={regionId} />
        </Panel>
        <Panel title={`Groups in ${reg.name}`} sub={`Avg FED comparison · ${cfg.subLabel}`}>
          <div className='flex flex-col gap-3'>
            {reg.groups.map((gid) => {
              const gw = workersInGroup(gid);
              const gFED = avgFED(gw);
              const gNR = notReadyCount(gw);
              return (
                <button
                  key={gid}
                  type='button'
                  onClick={() => drillGroup(gid)}
                  className='cursor-pointer rounded-lg border border-border bg-muted px-3 py-2.5 text-left transition-colors hover:bg-accent'
                >
                  <div className='mb-1.5 flex items-center justify-between'>
                    <span className='font-semibold'>{GROUPS[gid].name}</span>
                    <span className='text-[11px] text-muted-foreground'>
                      {gw.length} workers
                      {gNR > 0 && (
                        <>
                          {' '}
                          · <span className='text-destructive'>{gNR} not ready</span>
                        </>
                      )}
                    </span>
                  </div>
                  <CompBar val={gFED} max={100} color={toneVar[fedTone(gFED)]} />
                </button>
              );
            })}
          </div>
        </Panel>
      </Grid>
      <Panel title={`FED Heatmap — ${reg.name} Workers`} sub={`${cfg.subLabel} · click to drill in`}>
        <div className={dataTable.wrap}>
          <table className={dataTable.table}>
            <HeatHead cfg={cfg} lead={['Worker', 'Group']} />
            <tbody>
              {[...rw]
                .sort((a, b) => b.fed - a.fed)
                .map((w) => (
                  <tr
                    key={w.init}
                    className={clickRow}
                    onClick={() => patch({ activeWorker: w.init, drillScope: 'individual' })}
                  >
                    <td className={cn(dataTable.td, 'font-medium')}>{w.name}</td>
                    <td className={cn(dataTable.td, 'text-[11px] text-muted-foreground')}>{GROUPS[w.group].name}</td>
                    {cfg.heatIdx.map((i) => (
                      <td key={i} className={tdC}>
                        <HeatPill v={workerAt(cfg, w, i)} />
                      </td>
                    ))}
                    <td className={tdC}>
                      <HeatPill v={w.fed} strong />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
};

// ── Group ────────────────────────────────────────────────────────────────────

const GroupPast: React.FC<{ cfg: PastCfg; groupId: number }> = ({ cfg, groupId }) => {
  const { patch } = useDash();
  const grp = GROUPS[groupId];
  const rid = grp.region;
  const gw = workersInGroup(groupId);
  const gAvg = avgFED(gw);
  const gWtr = wtrBreaches(gw);
  const gNR = notReadyCount(gw);
  const [iw, ow] = cfg.inOutData[rid] ?? [0, 0];
  const byFed = [...gw].sort((a, b) => b.fed - a.fed);
  return (
    <>
      <KpiRow>
        <Kpi
          label='Not Ready'
          value={
            <>
              {gNR}
              <small className='text-base font-normal text-muted-foreground'>/{gw.length}</small>
            </>
          }
          sub={grp.name}
          tone={gNR > 0 ? 'bad' : 'good'}
        />
        <Kpi
          label={`Avg FED — ${cfg.subLabel}`}
          value={gAvg}
          sub={`vs region avg ${avgFED(workersInRegion(rid))}`}
          tone='warn'
        />
        <Kpi label='WTR Violations' value={gWtr} sub={cfg.subLabel} tone={gWtr > 0 ? 'bad' : 'good'} />
        <Kpi label='Avg Sleep' value={`${avgSleepOf(gw)}h`} sub='↓ vs 7.0h target' subTrend='up' tone='warn' />
      </KpiRow>
      <AiInsight>{genInsight('past', gw, { scopeName: grp.name })}</AiInsight>
      <Grid cols={2}>
        <Panel title={`FED Trend — ${REGIONS[rid].name}`} sub={cfg.subLabel}>
          <TrendChart
            labels={cfg.xLabels}
            min={20}
            max={90}
            height={180}
            series={[{ key: rid, name: REGIONS[rid].name, data: cfg.regionTrends[rid], color: REGIONS[rid].color }]}
          />
        </Panel>
        <Panel title='In-Work vs Out-of-Work' sub={`${grp.name} · ${cfg.subLabel}`}>
          {DEMO_MODE.live ? (
            <NeedsRota />
          ) : (
            <>
              <SplitHeader name={grp.name} inWork={iw} outWork={ow} />
              <SplitBar inWork={iw} outWork={ow} />
            </>
          )}
          <div className='mt-4'>
            <div className='mb-2 text-xs font-semibold text-muted-foreground'>Group Members by FED</div>
            {byFed.map((w) => (
              <div key={w.init} className='mb-1.5 flex items-center gap-2'>
                <span className='w-20 shrink-0 truncate text-xs'>{w.name.split(' ')[0]}</span>
                <div className='flex-1'>
                  <CompBar val={w.fed} max={100} color={toneVar[fedTone(w.fed)]} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </Grid>
      <Panel title={`FED Heatmap — ${grp.name}`} sub={`${cfg.subLabel} · click to view individual`}>
        <div className={dataTable.wrap}>
          <table className={dataTable.table}>
            <HeatHead cfg={cfg} lead={['Worker']} readinessCol />
            <tbody>
              {byFed.map((w) => {
                const rd = readiness(w);
                return (
                  <tr
                    key={w.init}
                    className={clickRow}
                    onClick={() => patch({ activeWorker: w.init, drillScope: 'individual' })}
                  >
                    <td className={cn(dataTable.td, 'font-medium')}>{w.name}</td>
                    {cfg.heatIdx.map((i) => (
                      <td key={i} className={tdC}>
                        <HeatPill v={workerAt(cfg, w, i)} />
                      </td>
                    ))}
                    <td className={tdC}>
                      <HeatPill v={w.fed} strong />
                    </td>
                    <td className={tdC}>
                      <Pill tone={rdyTone(rd)}>{rdyLabel(rd)}</Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
};

// ── Individual ───────────────────────────────────────────────────────────────

const IndividualPast: React.FC<{ cfg: PastCfg; initials: string }> = ({ cfg, initials }) => {
  const w = WORKERS[initials];
  const n = cfg.xLabels.length;
  const rd = readiness(w);
  const dHRV = pct(w.hrv, w.hrv_b);
  const dRHR = pct(w.rhr, w.rhr_b);
  const dSLP = pct(w.slp, w.slp_b);
  const wRid = GROUPS[w.group].region;
  const indivFed = cfg.heatIdx
    .map((i) => workerAt(cfg, w, i))
    .concat([w.fed])
    .slice(0, n);
  return (
    <>
      <KpiRow>
        <Kpi label='FED Score' value={w.fed} sub={GROUPS[w.group].name} tone={fedTone(w.fed)} />
        <Kpi
          label='HRV vs Baseline'
          value={`${sign(dHRV)}${dHRV}%`}
          sub={`${w.hrv} ms · base ${w.hrv_b} ms`}
          tone={lowBad(dHRV, -15, -8)}
        />
        <Kpi
          label='Sleep vs Baseline'
          value={`${sign(dSLP)}${dSLP}%`}
          sub={`${w.slp}h · base ${w.slp_b}h`}
          tone={lowBad(dSLP, -15, -8)}
        />
        <Kpi label='Readiness' value={<span className='text-2xl'>{rdyLabel(rd)}</span>} tone={rdyTone(rd)} />
      </KpiRow>
      <AiInsight>{genInsight('past', [w], { scopeName: w.name })}</AiInsight>
      <Grid cols={2}>
        <Panel title={`FED Trend — ${w.name}`} sub={`${cfg.subLabel} vs region`}>
          <TrendChart
            labels={cfg.xLabels}
            min={20}
            max={100}
            height={180}
            series={[
              { key: 'region', name: REGIONS[wRid].name, data: cfg.regionTrends[wRid], color: REGIONS[wRid].color },
              { key: 'worker', name: w.name, data: indivFed, color: toneVar.bad },
            ]}
          />
        </Panel>
        <Panel title='In-Work vs Out-of-Work' sub='Fatigue source split'>
          {DEMO_MODE.live ? <NeedsRota /> : <SplitBar inWork={w.inWorkFed} outWork={w.outWorkFed} tall />}
          <div className={cn('mt-2.5 flex justify-between', DEMO_MODE.live && 'hidden')}>
            <div>
              <span className='text-xl font-bold text-info'>{w.inWorkFed}%</span>
              <div className='text-[11px] text-muted-foreground'>In-work (shifts, driving)</div>
            </div>
            <div className='text-right'>
              <span className='text-xl font-bold text-violet'>{w.outWorkFed}%</span>
              <div className='text-[11px] text-muted-foreground'>Out-of-work (sleep, lifestyle)</div>
            </div>
          </div>
          <div className='mt-4 grid grid-cols-2 gap-2.5'>
            <div className='rounded-lg bg-muted p-2.5'>
              <div className='text-[11px] text-muted-foreground'>RHR vs baseline</div>
              <div className={cn('text-lg font-bold', toneText[highBad(dRHR, 8, 4)])}>
                {sign(dRHR)}
                {dRHR}%
              </div>
              <div className='text-[11px] text-muted-foreground'>
                {w.rhr} bpm · base {w.rhr_b}
              </div>
            </div>
            <div className='rounded-lg bg-muted p-2.5'>
              <div className='text-[11px] text-muted-foreground'>Consecutive days</div>
              <div className={cn('text-lg font-bold', toneText[highBad(w.consec, 6, 4)])}>{w.consec}</div>
              <div className='text-[11px] text-muted-foreground'>
                WTR:{' '}
                <span className={w.wtrFlag ? 'text-destructive' : 'text-success'}>{w.wtrFlag ? 'Breach' : 'OK'}</span>
              </div>
            </div>
          </div>
        </Panel>
      </Grid>
      <Panel title={`FED History — ${w.name}`} sub={cfg.subLabel}>
        <div className={dataTable.wrap}>
          <table className={dataTable.table}>
            <HeatHead cfg={cfg} lead={[]} />
            <tbody>
              <tr>
                {cfg.heatIdx.map((i) => (
                  <td key={i} className={tdC}>
                    <HeatPill v={workerAt(cfg, w, i)} />
                  </td>
                ))}
                <td className={tdC}>
                  <HeatPill v={w.fed} strong />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
};

// ── Screen ───────────────────────────────────────────────────────────────────

export const PastScreen: React.FC = () => {
  const dash = useDash();
  const range = (dash.pastRange in PAST_CFG ? dash.pastRange : '30d') as PastRange;
  const cfg = DEMO_MODE.live ? buildLivePastCfg(range) : PAST_CFG[range];

  let body: React.ReactNode;
  if (dash.drillScope === 'region' && dash.activeRegion) {
    body = <RegionPast cfg={cfg} regionId={dash.activeRegion} />;
  } else if (dash.drillScope === 'group' && dash.activeGroup !== null) {
    body = <GroupPast cfg={cfg} groupId={dash.activeGroup} />;
  } else if (dash.drillScope === 'individual' && dash.activeWorker) {
    body = <IndividualPast cfg={cfg} initials={dash.activeWorker} />;
  } else {
    body = <CompanyPast cfg={cfg} />;
  }

  return (
    <div className='flex flex-col gap-4'>
      {/* Prototype order: the individual picker sits above the period filter, region/group pickers below. */}
      {dash.drillScope === 'individual' ? (
        <>
          <ScopeSwitchers />
          <PeriodFilter range={range} />
        </>
      ) : (
        <>
          <PeriodFilter range={range} />
          <ScopeSwitchers />
        </>
      )}
      {body}
    </div>
  );
};
